import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface TimeSeriesDataPoint {
  date: string;
  value: number;
}

interface TimeSeriesMetric {
  total: number;
  label: string;
  data: TimeSeriesDataPoint[];
}

interface ListItem {
  label: string;
  value: number;
}

interface ListMetric {
  label: string;
  data: ListItem[];
}

export interface WebsiteAnalytics {
  timeSeries: {
    signups: TimeSeriesMetric;
    activeUsers: TimeSeriesMetric;
    revenue: TimeSeriesMetric;
    pageViews: TimeSeriesMetric;
    visitors: TimeSeriesMetric;
  };
  summary: {
    totalClients: number;
    totalActive: number;
    totalPaid: number;
    totalRevenue: number;
    newSignups: number;
    totalPageViews: number;
    uniqueVisitors: number;
    totalSessions: number;
    pagesPerSession: number;
    bounceRate: number;
  };
  lists: {
    plan: ListMetric;
    country: ListMetric;
    status: ListMetric;
    topPages: ListMetric;
    sources: ListMetric;
    devices: ListMetric;
  };
}

export type DatePreset = 'today' | '7d' | '30d' | '90d' | 'custom';

interface UseWebsiteAnalyticsReturn {
  analytics: WebsiteAnalytics | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  dateRange: { start: Date; end: Date };
  setDateRange: (start: Date, end: Date) => void;
  preset: DatePreset;
  setPreset: (preset: DatePreset) => void;
}

const countryNames: Record<string, string> = {
  AE: 'UAE', US: 'United States', CA: 'Canada', GB: 'United Kingdom',
  SG: 'Singapore', PT: 'Portugal', DE: 'Germany', FR: 'France',
  AU: 'Australia', IN: 'India', BR: 'Brazil', ES: 'Spain',
  IT: 'Italy', NL: 'Netherlands', JP: 'Japan', KR: 'South Korea',
  MX: 'Mexico', AL: 'Albania', JM: 'Jamaica', CU: 'Cuba', PH: 'Philippines',
  IR: 'Iran', Unknown: 'Unknown',
};

export const getCountryName = (code: string): string => {
  return countryNames[code] || code;
};

const getDateRangeFromPreset = (preset: DatePreset): { start: Date; end: Date } => {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date();
  start.setHours(0, 0, 0, 0);

  switch (preset) {
    case 'today':
      return { start, end };
    case '7d':
      start.setDate(start.getDate() - 6);
      return { start, end };
    case '30d':
      start.setDate(start.getDate() - 29);
      return { start, end };
    case '90d':
      start.setDate(start.getDate() - 89);
      return { start, end };
    default:
      return { start, end };
  }
};

export const useWebsiteAnalytics = (): UseWebsiteAnalyticsReturn => {
  const [analytics, setAnalytics] = useState<WebsiteAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preset, setPresetState] = useState<DatePreset>('30d');
  const [dateRange, setDateRangeState] = useState(getDateRangeFromPreset('30d'));

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch clients and page views in parallel
      const start = dateRange.start;
      const end = dateRange.end;

      const [clientsRes, pageViewsRes] = await Promise.all([
        supabase
          .from('clients')
          .select('id, name, email, plan, plan_price, status, paid, has_access, subscription_start, subscription_end, last_active_at, created_at, amount_paid, country'),
        supabase
          .from('page_views')
          .select('path, session_id, visitor_id, created_at, source, device_type')
          .gte('created_at', start.toISOString())
          .lte('created_at', end.toISOString()),
      ]);

      if (clientsRes.error) throw new Error(clientsRes.error.message);

      const allClients = (clientsRes.data || []).filter(
        c => !c.email.includes('test') && !c.email.includes('example')
      );
      const pageViews = pageViewsRes.data || [];

      const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      const granularity = daysDiff <= 2 ? 'hourly' : 'daily';

      // Generate date buckets
      const buckets: { date: string }[] = [];
      if (granularity === 'hourly') {
        for (let h = 0; h < 24; h++) {
          const d = new Date(start);
          d.setHours(h, 0, 0, 0);
          buckets.push({ date: d.toISOString() });
        }
      } else {
        const current = new Date(start);
        while (current <= end) {
          buckets.push({ date: current.toISOString().split('T')[0] });
          current.setDate(current.getDate() + 1);
        }
      }

      // Signups per bucket
      const signupData = buckets.map(b => {
        const count = allClients.filter(c => {
          const created = new Date(c.created_at);
          if (granularity === 'hourly') {
            const bd = new Date(b.date);
            return created.toDateString() === bd.toDateString() && created.getHours() === bd.getHours();
          }
          return created.toISOString().split('T')[0] === b.date;
        }).length;
        return { date: b.date, value: count };
      });

      // Active users per bucket
      const activeData = buckets.map(b => {
        const count = allClients.filter(c => {
          if (!c.last_active_at) return false;
          const active = new Date(c.last_active_at);
          if (granularity === 'hourly') {
            const bd = new Date(b.date);
            return active.toDateString() === bd.toDateString() && active.getHours() === bd.getHours();
          }
          return active.toISOString().split('T')[0] === b.date;
        }).length;
        return { date: b.date, value: count };
      });

      // Revenue per bucket
      const revenueData = buckets.map(b => {
        const rev = allClients
          .filter(c => {
            if (!c.amount_paid) return false;
            const created = new Date(c.created_at);
            if (granularity === 'hourly') {
              const bd = new Date(b.date);
              return created.toDateString() === bd.toDateString() && created.getHours() === bd.getHours();
            }
            return created.toISOString().split('T')[0] === b.date;
          })
          .reduce((sum, c) => sum + (c.amount_paid || 0), 0);
        return { date: b.date, value: rev };
      });

      // Page views per bucket
      const pvData = buckets.map(b => {
        const count = pageViews.filter(pv => {
          const created = new Date(pv.created_at);
          if (granularity === 'hourly') {
            const bd = new Date(b.date);
            return created.toDateString() === bd.toDateString() && created.getHours() === bd.getHours();
          }
          return created.toISOString().split('T')[0] === b.date;
        }).length;
        return { date: b.date, value: count };
      });

      // Unique visitors per bucket
      const visitorData = buckets.map(b => {
        const visitors = new Set(pageViews.filter(pv => {
          const created = new Date(pv.created_at);
          if (granularity === 'hourly') {
            const bd = new Date(b.date);
            return created.toDateString() === bd.toDateString() && created.getHours() === bd.getHours();
          }
          return created.toISOString().split('T')[0] === b.date;
        }).map(pv => pv.visitor_id).filter(Boolean));
        return { date: b.date, value: visitors.size };
      });

      // Clients in date range
      const rangeClients = allClients.filter(c => {
        const created = new Date(c.created_at);
        return created >= start && created <= end;
      });

      const totalActive = allClients.filter(c => c.status === 'active' || c.status === 'expiring').length;
      const totalPaid = allClients.filter(c => c.paid && c.amount_paid && c.amount_paid > 0).length;
      const totalRevenue = allClients.reduce((sum, c) => sum + (c.amount_paid || 0), 0);

      // Unique visitors & sessions
      const uniqueVisitors = new Set(pageViews.map(pv => pv.visitor_id).filter(Boolean)).size;
      const totalSessions = new Set(pageViews.map(pv => pv.session_id).filter(Boolean)).size;
      const pagesPerSession = totalSessions > 0 ? Math.round((pageViews.length / totalSessions) * 100) / 100 : 0;

      // Bounce rate: sessions with only 1 page view
      const sessionPageCounts: Record<string, number> = {};
      pageViews.forEach(pv => {
        if (pv.session_id) sessionPageCounts[pv.session_id] = (sessionPageCounts[pv.session_id] || 0) + 1;
      });
      const singlePageSessions = Object.values(sessionPageCounts).filter(c => c === 1).length;
      const bounceRate = totalSessions > 0 ? Math.round((singlePageSessions / totalSessions) * 100) : 0;

      // Top pages
      const pageMap: Record<string, number> = {};
      pageViews.forEach(pv => { pageMap[pv.path] = (pageMap[pv.path] || 0) + 1; });
      const topPages = Object.entries(pageMap).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);

      // Sources
      const sourceMap: Record<string, number> = {};
      pageViews.forEach(pv => { sourceMap[(pv as any).source || 'Direct'] = (sourceMap[(pv as any).source || 'Direct'] || 0) + 1; });
      const sources = Object.entries(sourceMap).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);

      // Devices
      const deviceMap: Record<string, number> = {};
      pageViews.forEach(pv => { deviceMap[(pv as any).device_type || 'unknown'] = (deviceMap[(pv as any).device_type || 'unknown'] || 0) + 1; });
      const devices = Object.entries(deviceMap).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);

      // Plan distribution
      const planMap: Record<string, number> = {};
      allClients.forEach(c => { planMap[c.plan] = (planMap[c.plan] || 0) + 1; });

      // Country distribution
      const countryMap: Record<string, number> = {};
      allClients.forEach(c => { countryMap[c.country || 'Unknown'] = (countryMap[c.country || 'Unknown'] || 0) + 1; });

      // Status distribution
      const statusMap: Record<string, number> = {};
      allClients.forEach(c => { statusMap[c.status] = (statusMap[c.status] || 0) + 1; });

      setAnalytics({
        timeSeries: {
          signups: { total: rangeClients.length, label: 'New Signups', data: signupData },
          activeUsers: { total: totalActive, label: 'Active Users', data: activeData },
          revenue: { total: totalRevenue, label: 'Revenue ($)', data: revenueData },
          pageViews: { total: pageViews.length, label: 'Page Views', data: pvData },
          visitors: { total: uniqueVisitors, label: 'Visitors', data: visitorData },
        },
        summary: {
          totalClients: allClients.length,
          totalActive,
          totalPaid,
          totalRevenue,
          newSignups: rangeClients.length,
          totalPageViews: pageViews.length,
          uniqueVisitors,
          totalSessions,
          pagesPerSession,
          bounceRate,
        },
        lists: {
          plan: { label: 'Plan Distribution', data: Object.entries(planMap).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value) },
          country: { label: 'Countries', data: Object.entries(countryMap).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value) },
          status: { label: 'Status', data: Object.entries(statusMap).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value) },
          topPages: { label: 'Top Pages', data: topPages },
          sources: { label: 'Traffic Sources', data: sources },
          devices: { label: 'Devices', data: devices },
        },
      });
    } catch (err: any) {
      console.error('Error fetching analytics:', err);
      setError(err.message || 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  const setDateRange = (start: Date, end: Date) => {
    setDateRangeState({ start, end });
    setPresetState('custom');
  };

  const setPreset = (p: DatePreset) => {
    setPresetState(p);
    if (p !== 'custom') {
      setDateRangeState(getDateRangeFromPreset(p));
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return { analytics, loading, error, refetch: fetchAnalytics, dateRange, setDateRange, preset, setPreset };
};

export default useWebsiteAnalytics;
